"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Tag, Share2, Bookmark } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";


function RelatedPosts({ currentPostId, categoryIds }: { currentPostId: string; categoryIds?: number[] }) {
  
  const [relatedQuery, setRelatedQuery] = useState<WP_Query | null>(null);

  useEffect(() => {
    const params: any = {
      post_type: "post",
      posts_per_page: 3,
      post__not_in: [parseInt(currentPostId)],
      orderby: "date",
      order: "DESC",
    };

    
    if (categoryIds && categoryIds.length > 0) {
      params.category__in = categoryIds;
    }

    setRelatedQuery(new WP_Query(params));
  }, [currentPostId, categoryIds]);

  if (!relatedQuery) return null;

  return (
    <PostsProvider wp_query={relatedQuery}>
      <RelatedPostsConsumer />
    </PostsProvider>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="34512" className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 bg-muted animate-pulse rounded-sm" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-serif font-bold text-foreground">Recommended Reading</h3>
        <Link to="/TheContentArchivePage">
          <Button variant="ghost" className="text-primary hover:text-primary/80">
            Browse the Archive <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.map((post, i) => (
          <Card key={post.id} data-index={i} className="overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow bg-card rounded-sm">
            <div className="relative aspect-video w-full overflow-hidden">
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="object-cover w-full h-full transition-transform duration-500 hover:scale-105"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                  <Tag className="h-10 w-10 opacity-20" />
                </div>
              )}
            </div>
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 font-sans uppercase tracking-wider">
                <Calendar className="h-3 w-3" />
                <span>{post.date ? new Date(post.date).toLocaleDateString("en-US") : ""}</span>
              </div>
              <Link to={post.link || "#"}>
                <CardTitle className="text-lg font-serif hover:text-primary transition-colors line-clamp-2 leading-snug">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div 
                className="text-sm text-muted-foreground line-clamp-3 font-sans leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            <CardFooter className="p-6 pt-0 mt-auto">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full group rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  Read Article 
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}


function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-1/3 bg-muted animate-pulse rounded-sm" />
        <div className="aspect-video w-full bg-muted animate-pulse rounded-sm" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted animate-pulse rounded-sm" />
          <div className="h-4 w-full bg-muted animate-pulse rounded-sm" />
          <div className="h-4 w-2/3 bg-muted animate-pulse rounded-sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h2 className="text-2xl font-serif font-bold text-destructive mb-4">Something went wrong</h2>
        <p className="text-muted-foreground font-sans">We couldn't load this content. Please try refreshing or check back soon.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Post Not Found</h2>
        <p className="text-muted-foreground font-sans">The article you're looking for might have been moved or archived.</p>
        <Link to="/HomePage">
          <Button className="mt-6 rounded-sm bg-primary text-primary-foreground">Back to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map(c => c.id) || [];

  return (
    <article className="max-w-4xl mx-auto px-4 py-12 md:py-20">
      {}
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-6 justify-center md:justify-start font-sans uppercase tracking-widest">
          {post.categories && post.categories.length > 0 && (
            <>
              <span className="bg-accent text-accent-foreground px-3 py-1 rounded-sm font-semibold">
                {post.categories[0].name}
              </span>
              <span className="text-muted-foreground/40">•</span>
            </>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>{post.date ? new Date(post.date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : ""}</span>
          </div>
          <span className="text-muted-foreground/40 hidden sm:inline">•</span>
          <div className="flex items-center gap-1 hidden sm:flex">
            <Clock className="h-4 w-4" />
            <span>5 min read</span>
          </div>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-tight mb-8">
          {post.title}
        </h1>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-y border-border py-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12 border border-border shadow-sm">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name || "Kaleigh"} />
              <AvatarFallback><User className="h-6 w-6" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground font-sans">{post.author?.name || "Kaleigh Johnson"}</p>
              <p className="text-xs text-muted-foreground font-sans uppercase tracking-tighter">Content Strategist</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="rounded-sm gap-2 font-sans">
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2 font-sans">
              <Bookmark className="h-4 w-4" />
              <span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-sm overflow-hidden shadow-lg border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="prose prose-lg prose-slate dark:prose-invert max-w-none mb-16 font-sans
        [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-muted-foreground
        [&_h2]:text-2xl [&_h2]:font-serif [&_h2]:font-bold [&_h2]:text-foreground [&_h2]:mt-10 [&_h2]:mb-4
        [&_h3]:text-xl [&_h3]:font-serif [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-8 [&_h3]:mb-3
        [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:text-muted-foreground
        [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:text-muted-foreground
        [&_li]:mb-2
        [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-foreground/80 [&_blockquote]:my-8 [&_blockquote]:font-serif
        [&_img]:rounded-sm [&_img]:shadow-sm [&_img]:my-8
        [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80
      ">
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-12">
          {post.tags.map((tag, i) => (
            <Link key={i} to={tag.link || "#"}>
              <span className="inline-flex items-center px-3 py-1 rounded-sm text-xs uppercase tracking-wider bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer font-sans">
                <Tag className="h-3 w-3 mr-2" />
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {}
      <div className="bg-card rounded-sm p-8 mb-16 flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left border border-border shadow-sm">
        <Avatar className="h-24 w-24 border border-border shadow-sm">
          <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name || "Kaleigh"} />
          <AvatarFallback className="text-2xl"><User /></AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h3 className="text-xl font-serif font-bold text-foreground mb-2">
            About {post.author?.name || "Kaleigh"}
          </h3>
          <p className="text-muted-foreground mb-4 leading-relaxed font-sans">
            {post.author?.description || "Kaleigh is a boutique content strategist specializing in translating complex concepts into clear, actionable messaging for tech, insurance, and ecommerce brands."}
          </p>
          <Link to={post.author?.link || "/AboutKaleighPage"}>
            <Button variant="link" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 font-sans">
              View More Articles <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {}
      <RelatedPosts currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}


export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}